import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSystemSettings } from '../hooks/useSystemSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectEditModal = ({ project, onClose, onProjectUpdated }) => {
  const { user } = useAuth();
  const { industries: systemIndustries, stages: systemStages, taskTemplates } = useSystemSettings();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    customer_company: '',
    client_company: '',
    gc_company: '',
    city: '',
    industry: 'energy',
    budget_amount: '',
    budget_currency: 'KZT',
    quarter: '',
    build_start_year: '',
    build_end_year: '',
    readiness_phase: 'concept',
    relation_status: 'no_contact',
    success_probability: '25',
    stage: 'research',
    flags: [],
    next_action: '',
    description: '',
    notes: '',
    sources: []
  });
  const [error, setError] = useState('');
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');

  // Use dynamic industries from system settings
  const industries = systemIndustries.map(industry => ({
    value: industry.id,
    label: industry.title
  }));

  const readinessOptions = [
    { value: 'concept', label: 'Концепт' },
    { value: 'design', label: 'Проектирование' },
    { value: 'construction_start', label: 'Начало строительства' },
    { value: 'active_construction', label: 'Активная стройка' },
    { value: 'approaching_em', label: 'Подходит к ЭМ' },
    { value: 'completion', label: 'Завершение' }
  ];

  const relationOptions = [
    { value: 'no_contact', label: 'Нет контакта' },
    { value: 'cold_contact', label: 'Холодный контакт' },
    { value: 'warm_contact', label: 'Теплый контакт' },
    { value: 'trusted_relationship', label: 'Доверительные отношения' },
    { value: 'negative', label: 'Негативные. Требуют улучшения' }
  ];

  const successOptions = [
    { value: '0', label: '0%' },
    { value: '25', label: '25%' },
    { value: '50', label: '50%' },
    { value: '75', label: '75%' },
    { value: '90', label: '90%' }
  ];

  // Use dynamic stages from system settings
  const stageOptions = systemStages.map(stage => ({
    value: stage.id,
    label: stage.title
  }));

  const flagOptions = [
    { value: 'hot', label: '🔥 Огонь' },
    { value: 'paused', label: '⏸️ Пауза' },
    { value: 'waiting_construction', label: '🏗️ Ждем стройготовность' },
    { value: 'waiting_funding', label: '💰 Ждут финансирования' },
    { value: 'risk', label: '⚠️ Риск срыва' }
  ];

  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name || '',
        customer_company: project.customer_company || '',
        designer_company: project.designer_company || '',
        gc_company: project.gc_company || '',
        subcontractor_company: project.subcontractor_company || '',
        city: project.city || '',
        industry: project.industry || 'energy',
        budget_amount: project.budget_amount || '',
        budget_currency: project.budget_currency || 'KZT',
        quarter: project.quarter || '',
        build_start_year: project.build_start_year || '',
        build_end_year: project.build_end_year || '',
        readiness_phase: project.readiness_phase || 'idea',
        relation_status: project.relation_status || 'no_contact',
        success_probability: project.success_probability || '25',
        stage: project.stage || 'research',
        flags: project.flags || [],
        next_action: project.next_action || '',
        description: project.description || '',
        notes: project.notes || '',
        sources: project.sources || []
      });
      loadComments();
    }
  }, [project]);

  const loadComments = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/comments`);
      // API returns {comments: [...], count: N}, so we need to extract the comments array
      setComments(response.data.comments || []);
    } catch (error) {
      console.error('Failed to load comments:', error);
      setComments([]); // Set empty array on error
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'flags') {
      const newFlags = checked 
        ? [...formData.flags, value]
        : formData.flags.filter(f => f !== value);
      setFormData({ ...formData, flags: newFlags });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'number' ? parseFloat(value) || '' : value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const updateData = {
        ...formData,
        budget_amount: formData.budget_amount ? parseFloat(formData.budget_amount) : null,
        build_start_year: formData.build_start_year ? parseInt(formData.build_start_year) : null,
        build_end_year: formData.build_end_year ? parseInt(formData.build_end_year) : null
      };

      const response = await axios.put(`${API}/projects/${project.id}`, updateData);
      onProjectUpdated(response.data);
      onClose();
    } catch (err) {
      console.error('Failed to update project:', err);
      setError(err.response?.data?.detail || 'Не удалось обновить проект');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      await axios.post(`${API}/projects/${project.id}/comments`, {
        comment: newComment
      });
      setNewComment('');
      loadComments();
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  const formatCurrency = (amount, currency = 'KZT') => {
    if (!amount) return 'Не указано';
    
    if (currency === 'KZT') {
      return new Intl.NumberFormat('ru-RU').format(amount) + ' ₸';
    } else if (currency === 'USD') {
      return new Intl.NumberFormat('ru-RU').format(amount) + ' ₸';
    } else {
      return new Intl.NumberFormat('ru-RU').format(amount) + ' ' + currency;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl min-h-[90vh] my-8" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              Редактировать проект: {project?.name}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              data-testid="close-modal-button"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-medium mb-4">Основная информация</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-group">
                      <label className="form-label">Проект *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="form-input"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Город *</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className="form-input"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Компания (кто покупает)</label>
                      <input
                        type="text"
                        name="customer_company"
                        value={formData.customer_company}
                        onChange={handleChange}
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Проектировщик</label>
                      <input
                        type="text"
                        name="designer_company"
                        value={formData.designer_company}
                        onChange={handleChange}
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Генподрядчик</label>
                      <input
                        type="text"
                        name="gc_company"
                        value={formData.gc_company}
                        onChange={handleChange}
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Субподрядчик</label>
                      <input
                        type="text"
                        name="subcontractor_company"
                        value={formData.subcontractor_company}
                        onChange={handleChange}
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Отрасль</label>
                      <select
                        name="industry"
                        value={formData.industry}
                        onChange={handleChange}
                        className="form-select"
                      >
                        {industries.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Financial Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-medium mb-4">Финансовая информация</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-group">
                      <label className="form-label">Бюджет проекта (₸)</label>
                      <input
                        type="number"
                        name="budget_amount"
                        value={formData.budget_amount}
                        onChange={handleChange}
                        className="form-input"
                        min="0"
                        placeholder="Введите сумму в тенге"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Сумма в тенге:</label>
                      <div className="p-3 bg-green-100 rounded border text-green-800 font-medium">
                        {formData.budget_amount ? new Intl.NumberFormat('ru-RU').format(formData.budget_amount) + ' ₸' : 'Не указано'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-medium mb-4">Сроки реализации</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-group">
                      <label className="form-label">Квартал реализации</label>
                      <select
                        name="quarter"
                        value={formData.quarter}
                        onChange={handleChange}
                        className="form-select"
                      >
                        <option value="">Не указан</option>
                        <option value="Q1">Q1 (Январь-Март)</option>
                        <option value="Q2">Q2 (Апрель-Июнь)</option>
                        <option value="Q3">Q3 (Июль-Сентябрь)</option>
                        <option value="Q4">Q4 (Октябрь-Декабрь)</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Год реализации</label>
                      <input
                        type="number"
                        name="build_start_year"
                        value={formData.build_start_year}
                        onChange={handleChange}
                        className="form-input"
                        min="2024"
                        max="2030"
                        placeholder="2025"
                      />
                    </div>
                  </div>
                </div>

                {/* Project Management */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-medium mb-4">Управление проектом</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-group">
                      <label className="form-label">Этап</label>
                      <select
                        name="stage"
                        value={formData.stage}
                        onChange={handleChange}
                        className="form-select"
                      >
                        {stageOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Отношения</label>
                      <select
                        name="relation_status"
                        value={formData.relation_status}
                        onChange={handleChange}
                        className="form-select"
                      >
                        {relationOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Вероятность успеха</label>
                      <select
                        name="success_probability"
                        value={formData.success_probability}
                        onChange={handleChange}
                        className="form-select"
                      >
                        {successOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Фаза готовности</label>
                      <select
                        name="readiness_phase"
                        value={formData.readiness_phase}
                        onChange={handleChange}
                        className="form-select"
                      >
                        {readinessOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Tags */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-medium mb-4">Теги</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {flagOptions.map(option => (
                      <label key={option.value} className="flex items-center">
                        <input
                          type="checkbox"
                          name="flags"
                          value={option.value}
                          checked={formData.flags.includes(option.value)}
                          onChange={handleChange}
                          className="mr-2"
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-medium mb-4">Следующие действия</h3>
                  
                  <div className="form-group">
                    <label className="form-label">Описание проекта</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      className="form-textarea"
                      rows={3}
                      placeholder="Краткое описание проекта..."
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Что делать</label>
                    {/* Quick select from templates */}
                    {taskTemplates && taskTemplates.length > 0 && (
                      <div className="mb-2">
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              setFormData({
                                ...formData,
                                next_action: e.target.value
                              });
                              e.target.value = ''; // Reset select
                            }
                          }}
                          className="form-select text-sm"
                        >
                          <option value="">Выбрать из шаблонов...</option>
                          {taskTemplates.map((template, index) => (
                            <option key={index} value={template}>{template}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    {/* Free text input */}
                    <textarea
                      name="next_action"
                      value={formData.next_action}
                      onChange={handleChange}
                      className="form-textarea"
                      rows={3}
                      placeholder="Или введите свою задачу..."
                    />
                  </div>
                </div>

                {/* Save/Cancel Buttons */}
                <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn-secondary"
                    disabled={loading}
                  >
                    Закрыть
                  </button>
                  <button
                    type="submit"
                    className="btn-primary disabled:opacity-50"
                    disabled={loading}
                  >
                    {loading ? 'Сохранение...' : 'Сохранить'}
                  </button>
                </div>
              </form>
            </div>

            {/* Right Sidebar - Comments & Files */}
            <div className="space-y-6">
              {/* Comments Log */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-medium mb-4">Комментарии (лог)</h3>
                
                {/* Add Comment */}
                <div className="mb-4">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Добавить комментарий..."
                    className="form-textarea text-sm"
                    rows={2}
                  />
                  <button
                    type="button"
                    onClick={handleAddComment}
                    className="btn-primary text-sm mt-2"
                    disabled={!newComment.trim()}
                  >
                    Добавить
                  </button>
                </div>

                {/* Comments List */}
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {Array.isArray(comments) && comments.map(comment => (
                    <div key={comment.id} className="bg-white p-3 rounded border">
                      <div className="text-sm text-gray-900">{comment.comment}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {comment.author_name} • {new Date(comment.created_at).toLocaleString('ru-RU')}
                      </div>
                    </div>
                  ))}
                  
                  {(!Array.isArray(comments) || comments.length === 0) && (
                    <div className="text-center text-gray-500 py-4">
                      Нет комментариев
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectEditModal;